import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './Reveal.css';

/* Reveal a little before the element's top edge reaches the fold, so a chapter
   has already settled by the time the recruiter is reading it. */
const REVEAL_ROOT_MARGIN = '0px 0px -10% 0px';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/*
 * useRevealOnce
 * Reveal-on-first-view built from IntersectionObserver + CSS classes, written
 * so that content visibility never depends on an animation completing.
 *
 * The default rendered state is fully visible. The hidden state is only ever
 * applied by JS, in a layout effect (i.e. before paint, so nothing flashes),
 * and only when we know we will be able to undo it. It is skipped entirely
 * when there is no IntersectionObserver, when reduced motion is requested, or
 * when the document is hidden at mount — a background tab does not run the
 * rendering steps that drive IntersectionObserver, which is exactly how
 * content ends up stranded at opacity 0.
 *
 * Phases: 'idle' (visible, untouched) -> 'hidden' -> 'in' -> 'done'.
 *
 * Two details matter for that guarantee:
 *  - the stagger is a JS timer, not animation-delay, so the reveal animation
 *    needs no fill mode. An animation that is pending or never runs therefore
 *    contributes nothing and the element is simply visible. (A filled
 *    animation would pin the element to its `from` state — opacity 0 — which
 *    is exactly how content gets stranded.)
 *  - the animating class is dropped on completion, so the element's own
 *    transforms, such as card hover lifts, are never overridden.
 */
export function useRevealOnce(delay = 0) {
  const ref = useRef(null);
  const [phase, setPhase] = useState('idle');

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.IntersectionObserver === 'undefined') return;
    if (prefersReducedMotion()) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    setPhase('hidden');
  }, []);

  useEffect(() => {
    if (phase !== 'hidden') return undefined;
    const node = ref.current;
    if (!node) return undefined;

    let timer;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          /* the stagger lives here rather than in animation-delay — see above */
          timer = window.setTimeout(() => setPhase('in'), Math.max(0, delay) * 1000);
        }
      },
      { threshold: 0, rootMargin: REVEAL_ROOT_MARGIN }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [phase, delay]);

  /* While anything is mid-reveal, two things can strand it: the tab going to
     the background (a hidden document creates the animation but never
     advances it, so it would sit on its 0% frame), or an animationend that
     never arrives. Both resolve to the same answer — abandon the animation
     and show the element. */
  useEffect(() => {
    if (phase !== 'hidden' && phase !== 'in') return undefined;

    const settle = () => setPhase('done');
    const onVisibilityChange = () => {
      if (document.hidden) settle();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    /* only once the animation is actually meant to be running — elements
       still waiting to be scrolled to must keep waiting */
    const backstop = phase === 'in' ? window.setTimeout(settle, 2000) : undefined;

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearTimeout(backstop);
    };
  }, [phase]);

  const handleAnimationEnd = useCallback((event) => {
    /* only our own reveal animation, never one bubbling up from a child */
    if (event.target === event.currentTarget) setPhase('done');
  }, []);

  return { ref, phase, handleAnimationEnd };
}

/*
 * FadeIn
 * The single reveal used by every chapter: fade up once, on first view.
 * See useRevealOnce for the accessibility / fail-safe behaviour.
 */
export function FadeIn({
  as: Tag = 'div',
  className = '',
  children,
  delay = 0,
  y = 20,
  style,
  ...rest
}) {
  const { ref, phase, handleAnimationEnd } = useRevealOnce(delay);

  const classNames = [
    className,
    phase === 'hidden' && 'reveal-pending',
    phase === 'in' && 'reveal-run',
  ]
    .filter(Boolean)
    .join(' ');

  const animating = phase === 'hidden' || phase === 'in';

  return (
    <Tag
      ref={ref}
      className={classNames || undefined}
      style={animating ? { ...style, '--reveal-y': `${y}px` } : style}
      onAnimationEnd={animating ? handleAnimationEnd : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Hover-lift card. Respects reduced-motion. */
export function HoverCard({ as = 'article', className, children, lift = -6, ...rest }) {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.article;

  return (
    <MotionTag
      className={className}
      whileHover={reduce ? undefined : { y: lift }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.7 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
